"use client";
import React, { useEffect, useState } from "react";
import profile from "./profile.module.css";
import watch from "../watchnow/watch.module.css";
import validator from "../utils/validator";
import { uploadImage } from "../utils/imgupload";
import { useAuthStore } from "../store/authstore";
import { fetchWithLoader } from "../utils/api";
import { useLoaderStore, useToastStore } from "../store/localapi";
import { useRouter } from "next/navigation";
import { processImageToJpegBlob } from "../utils/imageProcess";

export default function Profile() {
  const validate = validator();
  const { showToast } = useToastStore();
  const { login } = useAuthStore()
  const { showLoader, hideLoader } = useLoaderStore()
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const [userimg, setUserimg] = useState<string>("");
  const [userName, setUserName] = useState<string>(user?.user_name || "");
  const [gender, setUserGender] = useState<string>(user?.user_gender || "");
  const [dob, setDob] = useState<string>(user?.user_dob || "");
  const [displayDob, setDisplayDob] = useState<string>("");
  const [isClient, setIsClient] = useState(false);
  const [error, setError] = useState<{ userimg: string; userName: string; dob: string; }>({
    userimg: "",
    userName: "",
    dob: "",
  });

  // Promise timeout helper to avoid infinite loader on slow network/uploads
  const withTimeout = <T,>(p: Promise<T>, ms: number) => {
    return new Promise<T>((resolve, reject) => {
      const t = setTimeout(() => reject(new Error('Upload timed out')), ms);
      p.then((v) => { clearTimeout(t); resolve(v); })
        .catch((e) => { clearTimeout(t); reject(e); });
    });
  };


  // Convert yyyy-mm-dd to dd/mm/yyyy for display
  const formatDateForDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  useEffect(() => setIsClient(true), []);
  useEffect(() => {
    if (isClient) {
      if (user?.user_dob) {
        setDob(user.user_dob);
      }
      if (user?.user_image) {
        setUserimg(user.user_image);
      }
      if (user?.user_name) {
        setUserName(user.user_name);
      }
      if (user?.user_gender) {
        setUserGender(user.user_gender);
      }
      setTimeout(() => !user && router.push('/'), 50);
    }
  }, [user, isClient, router]);

  useEffect(() => {
    if (dob) {
      setDisplayDob(formatDateForDisplay(dob));
    }
  }, [dob]);


  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    showLoader()
    const inputEl = e.target;
    const file = inputEl.files?.[0];
    if (file) {
      try {
        // Process image to oriented JPEG Blob (more reliable on mobile) and resize large images
        const jpegBlob = await processImageToJpegBlob(file, { quality: 0.9, maxDimension: 1920, background: '#ffffff' });
        const processedFile = new File([jpegBlob], `${file.name.replace(/\.[^.]+$/, '') || 'image'}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });

        // Upload only the processed (orientation-corrected) file
        const imageUrl = await withTimeout(uploadImage(processedFile), 30000);
        if (imageUrl) {
          setUserimg(imageUrl);
          // console.log('Uploaded image URL:', imageUrl);
        }
        setError((prev) => ({ ...prev, userimg: "" }));
      } catch (error) {
        showToast("Image upload failed. Please try another format or size", "error")
        console.error("Image upload failed:", error);
      }
      finally {
        hideLoader();
        // Clear the input so selecting same file again re-triggers change
        try { inputEl.value = ""; } catch { }
      }
    } else {
      setUserimg("");
      hideLoader()
      setError((prev) => ({ ...prev, userimg: "Profile image is required" }));
    }
  };

  const updateData = async () => {
    const res = await fetch("https://dramasstory.com/web/user_profile.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user?.user_id }),
    })
    const data = await res.json();
    hideLoader()
    login({ ...user, ...data.data.Data });
    showToast("Profile updated successfully", "success");
    setTimeout(() => {
      router.push("/dashboard");
    }, 500);
    return;
  }

  const handleSubmit = (e: React.FormEvent) => {
    showLoader()
    e.preventDefault();
    // showLoader()
    let hasError = false;

    if (!userimg) {
      setError((prev) => ({ ...prev, userimg: "Profile image is required" }));
      hasError = true;
    } else {
      hasError = false;
      setError((prev) => ({ ...prev, userimg: "" }));
    }

    const usernameError = validate.username(userName);
    if (usernameError) {
      setError((prev) => ({ ...prev, userName: usernameError }));
      hasError = true;
    } else {
      setError((prev) => ({ ...prev, userName: "" }));
    }

    if (validate.dob(dob)) {
      setError((prev) => ({ ...prev, dob: validate.dob(dob) ?? "" }));
      hasError = true;
    } else {
      hasError = false;
      setError((prev) => ({ ...prev, dob: "" }));
    }
    if (!hasError && !usernameError) {
      // Submit the form
      fetchWithLoader("drama_update_profile.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_email: user?.user_email,
          user_image: userimg,
          user_name: userName,
          user_gender: gender,
          user_dob: dob,
        }),
      })
        .then((response) => response.json())
        .then(() => {
          updateData();
        })
        .catch((error) => {
          console.error("Error updating profile:", error);
          showToast(error.message, "error");
          hideLoader()
        });
    } else {
      hideLoader()
    }
  };

  return (
    <div>
      <div className={watch.watchNow}>
        <h2>Profile</h2>
      </div>
      <div className={`${profile.UserProfile} editprofileMain`}>
        <div className={profile.parent}>
          <form onSubmit={handleSubmit}>
            <div className={profile.proMain}>
              <div className={profile.Profield}>
                <div className={profile.field}>
                  <div className="name">
                    <input
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      onBlur={() => {
                        setError((prev) => ({
                          ...prev,
                          userName: validate.username(userName) ?? "",
                        }));
                      }}
                      name="name"
                      id="name"
                      placeholder="Username"
                      required
                    />
                    <span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="21"
                        height="21"
                        viewBox="0 0 21 21"
                        fill="none"
                      >
                        <mask
                          id="mask0_2591_545"
                          mask-type="luminance"
                          maskUnits="userSpaceOnUse"
                          x="1"
                          y="0"
                          width="20"
                          height="21"
                        >
                          <path
                            d="M1.05469 0.439819H20.1735V19.5586H1.05469V0.439819Z"
                            fill="white"
                          />
                        </mask>
                        <g mask="url(#mask0_2591_545)">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7.17872 18.0649C5.52885 18.0649 4.19141 16.7275 4.19141 15.0776V4.92078C4.19141 3.27092 5.52885 1.93347 7.17872 1.93347H14.0495C15.6994 1.93347 17.0368 3.27092 17.0368 4.92078V15.0776C17.0368 16.7275 15.6994 18.0649 14.0495 18.0649H7.17872ZM2.69775 15.0776C2.69775 17.5524 4.70393 19.5586 7.17872 19.5586H14.0495C16.5243 19.5586 18.5305 17.5524 18.5305 15.0776V4.92078C18.5305 2.446 16.5243 0.439819 14.0495 0.439819H7.17872C4.70393 0.439819 2.69775 2.446 2.69775 4.92078V15.0776Z"
                            fill="#9B9D9C"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7.62671 15.334C8.03917 15.334 8.37354 14.9996 8.37354 14.5872C8.37354 13.506 9.26149 12.6122 10.3608 12.6122H10.8672C11.9665 12.6122 12.8545 13.506 12.8545 14.5872C12.8545 14.9996 13.1889 15.334 13.6013 15.334C14.0138 15.334 14.3482 14.9996 14.3482 14.5872C14.3482 12.6775 12.7879 11.1186 10.8672 11.1186H10.3608C8.44018 11.1186 6.87988 12.6775 6.87988 14.5872C6.87988 14.9996 7.21425 15.334 7.62671 15.334Z"
                            fill="#9B9D9C"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M8.00049 7.19814C8.00049 8.68301 9.2042 9.88672 10.6891 9.88672C12.1739 9.88672 13.3776 8.68301 13.3776 7.19814C13.3776 5.71327 12.1739 4.50956 10.6891 4.50956C9.2042 4.50956 8.00049 5.71327 8.00049 7.19814ZM10.6891 8.39306C10.0291 8.39306 9.49414 7.85809 9.49414 7.19814C9.49414 6.5382 10.0291 6.00322 10.6891 6.00322C11.349 6.00322 11.884 6.5382 11.884 7.19814C11.884 7.85809 11.349 8.39306 10.6891 8.39306Z"
                            fill="#9B9D9C"
                          />
                        </g>
                      </svg>
                    </span>
                  </div>
                  <p
                    className={profile.error}
                    style={{ opacity: error.userName ? 1 : 0 }}
                  >
                    {error.userName}.
                  </p>
                </div>

                <div className={profile.field}>
                  <div className="gender">
                    <select
                      name="gender"
                      value={gender}
                      onChange={(e) => {
                        setUserGender(e.target.value);
                      }}
                    >
                      <option value="">Select Gender</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                      <option value="other">Other</option>
                    </select>
                    <span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="21"
                        height="21"
                        viewBox="0 0 21 21"
                        fill="none"
                      >
                        <path
                          d="M10.6143 6.40272C10.6143 4.24401 8.86405 2.49414 6.70534 2.49414C4.54664 2.49414 2.79639 4.24401 2.79639 6.40272C2.79639 8.33887 4.20537 9.94215 6.05411 10.2529V14.3321L5.02075 13.2995L4.09963 14.2206L6.70534 16.8267L9.31144 14.2206L8.38994 13.2991L7.35696 14.3317V10.2525C9.20494 9.94177 10.6147 8.33849 10.6147 6.40233L10.6143 6.40272ZM6.70534 9.00881C5.26582 9.00881 4.09963 7.84185 4.09963 6.40272C4.09963 4.96358 5.26582 3.797 6.70534 3.797C8.14486 3.797 9.31144 4.96358 9.31144 6.40272C9.31144 7.84185 8.14448 9.00881 6.70534 9.00881Z"
                          fill="#9B9D9C"
                        />
                        <path
                          d="M15.1748 10.3711V7.05459H17.1293V5.75135H15.1748V3.79688H13.872V5.75135H11.9175V7.05459H13.872V10.3711C12.0236 10.6822 10.6143 12.2855 10.6143 14.2213C10.6143 16.38 12.3637 18.1302 14.5232 18.1302C16.6827 18.1302 18.4322 16.38 18.4322 14.2213C18.4322 12.2855 17.022 10.6822 15.1745 10.3711H15.1748ZM14.5232 16.827C13.0837 16.827 11.9171 15.66 11.9171 14.2209C11.9171 12.7818 13.0833 11.6148 14.5232 11.6148C15.9631 11.6148 17.1293 12.7818 17.1293 14.2209C17.1293 15.66 15.9624 16.827 14.5232 16.827Z"
                          fill="#9B9D9C"
                        />
                      </svg>
                    </span>

                    {/* <span className={profile.downarrow}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="10"
                        viewBox="0 0 18 10"
                        fill="none"
                      >
                        <path
                          d="M17.2007 1.31662C17.2343 1.63014 17.1066 1.95161 16.9255 2.19506L9.70188 9.62929C9.28922 10.0114 8.62672 10.0476 8.20283 9.65674L0.900566 2.14088C0.158058 1.06595 1.31744 -0.188863 2.41646 0.472134L8.93902 7.16735L15.4307 0.493806C16.1255 -0.00754055 17.108 0.444683 17.2007 1.31662Z"
                          fill="#9B9D9C"
                        />
                      </svg>
                    </span> */}
                  </div>
                  <p className={profile.error} style={{ opacity: 0 }}>
                    .
                  </p>
                </div>
              </div>

              <div className={profile.Profield}>
                <div className={profile.field}>
                  <div className={`${profile.dob} date`}>
                    <input
                      type="date"
                      value={dob}
                      placeholder=""
                      max={new Date().toISOString().split('T')[0]}
                      onChange={(e) => {
                        setDob(e.target.value);
                        setDisplayDob(formatDateForDisplay(e.target.value));
                      }}
                      onBlur={() => {
                        setError((prev) => ({
                          ...prev,
                          dob: validate.dob(dob) ?? "",
                        }));
                      }}
                      name="dateofbirth"
                      id="dateofbirth"
                    />
                    {!dob ? (
                      <span className="placeHolder">DD/MM/YYYY</span>
                    ) : (
                      displayDob && <span className="dateDisplay">{displayDob}</span>
                    )}
                    <span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="21"
                        height="21"
                        viewBox="0 0 21 21"
                        fill="none"
                      >
                        <path
                          d="M14.5788 9.34704C14.9157 9.34704 15.1888 9.07392 15.1888 8.737C15.1888 8.40008 14.9157 8.12695 14.5788 8.12695C14.2419 8.12695 13.9688 8.40008 13.9688 8.737C13.9688 9.07392 14.2419 9.34704 14.5788 9.34704Z"
                          fill="#9B9D9C"
                        />
                        <path
                          d="M15.9826 3.55212H15.1896V2.94208C15.1896 2.60503 14.9166 2.33203 14.5795 2.33203C14.2425 2.33203 13.9695 2.60503 13.9695 2.94208V3.55212H11.1938V2.94208C11.1938 2.60503 10.9208 2.33203 10.5837 2.33203C10.2467 2.33203 9.9737 2.60503 9.9737 2.94208V3.55212H7.22849V2.94208C7.22849 2.60503 6.9555 2.33203 6.61845 2.33203C6.2814 2.33203 6.0084 2.60503 6.0084 2.94208V3.55212H5.24584C3.90039 3.55212 2.80566 4.64685 2.80566 5.9923V15.509C2.80566 16.8545 3.90039 17.9492 5.24584 17.9492H9.91269C10.2497 17.9492 10.5227 17.6762 10.5227 17.3391C10.5227 17.0021 10.2497 16.7291 9.91269 16.7291H5.24584C4.57297 16.7291 4.02575 16.1819 4.02575 15.509V5.9923C4.02575 5.31942 4.57297 4.77221 5.24584 4.77221H6.0084V5.38226C6.0084 5.71931 6.2814 5.9923 6.61845 5.9923C6.9555 5.9923 7.22849 5.71931 7.22849 5.38226V4.77221H9.9737V5.38226C9.9737 5.71931 10.2467 5.9923 10.5837 5.9923C10.9208 5.9923 11.1938 5.71931 11.1938 5.38226V4.77221H13.9695V5.38226C13.9695 5.71931 14.2425 5.9923 14.5795 5.9923C14.9166 5.9923 15.1896 5.71931 15.1896 5.38226V4.77221H15.9826C16.6555 4.77221 17.2027 5.31942 17.2027 5.9923V9.46956C17.2027 9.80661 17.4757 10.0796 17.8128 10.0796C18.1498 10.0796 18.4228 9.80661 18.4228 9.46956V5.9923C18.4228 4.64685 17.3281 3.55212 15.9826 3.55212Z"
                          fill="#9B9D9C"
                        />
                        <path
                          d="M14.7318 10.5664C12.6967 10.5664 11.041 12.2221 11.041 14.2572C11.041 16.2923 12.6967 17.948 14.7318 17.948C16.7669 17.948 18.4226 16.2923 18.4226 14.2572C18.4226 12.2221 16.7669 10.5664 14.7318 10.5664ZM14.7318 16.7279C13.3696 16.7279 12.2611 15.6194 12.2611 14.2572C12.2611 12.8949 13.3696 11.7865 14.7318 11.7865C16.094 11.7865 17.2025 12.8949 17.2025 14.2572C17.2025 15.6194 16.094 16.7279 14.7318 16.7279Z"
                          fill="#9B9D9C"
                        />
                        <path
                          d="M15.6157 13.6451H15.3412V13.0046C15.3412 12.6675 15.0682 12.3945 14.7311 12.3945C14.3941 12.3945 14.1211 12.6675 14.1211 13.0046V14.2552C14.1211 14.5922 14.3941 14.8652 14.7311 14.8652H15.6157C15.9528 14.8652 16.2257 14.5922 16.2257 14.2552C16.2257 13.9181 15.9528 13.6451 15.6157 13.6451Z"
                          fill="#9B9D9C"
                        />
                        <path
                          d="M11.9255 9.34704C12.2624 9.34704 12.5355 9.07392 12.5355 8.737C12.5355 8.40008 12.2624 8.12695 11.9255 8.12695C11.5886 8.12695 11.3154 8.40008 11.3154 8.737C11.3154 9.07392 11.5886 9.34704 11.9255 9.34704Z"
                          fill="#9B9D9C"
                        />
                        <path
                          d="M9.27215 11.9994C9.60907 11.9994 9.8822 11.7263 9.8822 11.3893C9.8822 11.0524 9.60907 10.7793 9.27215 10.7793C8.93524 10.7793 8.66211 11.0524 8.66211 11.3893C8.66211 11.7263 8.93524 11.9994 9.27215 11.9994Z"
                          fill="#9B9D9C"
                        />
                        <path
                          d="M6.61835 9.34704C6.95526 9.34704 7.22839 9.07392 7.22839 8.737C7.22839 8.40008 6.95526 8.12695 6.61835 8.12695C6.28143 8.12695 6.0083 8.40008 6.0083 8.737C6.0083 9.07392 6.28143 9.34704 6.61835 9.34704Z"
                          fill="#9B9D9C"
                        />
                        <path
                          d="M6.61835 11.9994C6.95526 11.9994 7.22839 11.7263 7.22839 11.3893C7.22839 11.0524 6.95526 10.7793 6.61835 10.7793C6.28143 10.7793 6.0083 11.0524 6.0083 11.3893C6.0083 11.7263 6.28143 11.9994 6.61835 11.9994Z"
                          fill="#9B9D9C"
                        />
                        <path
                          d="M6.61835 14.6537C6.95526 14.6537 7.22839 14.3806 7.22839 14.0436C7.22839 13.7067 6.95526 13.4336 6.61835 13.4336C6.28143 13.4336 6.0083 13.7067 6.0083 14.0436C6.0083 14.3806 6.28143 14.6537 6.61835 14.6537Z"
                          fill="#9B9D9C"
                        />
                        <path
                          d="M9.27215 14.6537C9.60907 14.6537 9.8822 14.3806 9.8822 14.0436C9.8822 13.7067 9.60907 13.4336 9.27215 13.4336C8.93524 13.4336 8.66211 13.7067 8.66211 14.0436C8.66211 14.3806 8.93524 14.6537 9.27215 14.6537Z"
                          fill="#9B9D9C"
                        />
                        <path
                          d="M9.27215 9.34704C9.60907 9.34704 9.8822 9.07392 9.8822 8.737C9.8822 8.40008 9.60907 8.12695 9.27215 8.12695C8.93524 8.12695 8.66211 8.40008 8.66211 8.737C8.66211 9.07392 8.93524 9.34704 9.27215 9.34704Z"
                          fill="#9B9D9C"
                        />
                      </svg>
                    </span>
                  </div>
                  <p className={profile.error} style={{ opacity: error.dob ? 1 : 0 }}>
                    {error.dob}.
                  </p>
                </div>

                <div className={`${profile.field} ${profile.UploadIcon}`}>
                  {/* <input type="file" id="avatar" name="avatar" accept="image/png, image/jpeg" /> */}

                  <div className={profile.inputContainer}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      id="fileInput"
                      className={profile.customFileInput}
                    />
                    <label htmlFor="fileInput" className={profile.Clabel}>
                      {userimg == "" ? "Choose Photo" : userimg.split("/").pop()?.slice(-25)}

                      <span className="downarrow">
                        {userimg == "" ?
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="59"
                            height="56"
                            viewBox="0 0 59 56"
                            fill="none"
                          >
                            <rect
                              x="0.909668"
                              y="0.595703"
                              width="57.1069"
                              height="55"
                              rx="2"
                              fill="#E21220"
                            />
                            <path
                              d="M23.1373 23.2385L27.9899 18.3861V35.4589C27.9899 35.8497 28.1451 36.2245 28.4215 36.5009C28.6978 36.7772 29.0726 36.9325 29.4634 36.9325C29.8543 36.9325 30.2291 36.7772 30.5054 36.5009C30.7818 36.2245 30.937 35.8497 30.937 35.4589V18.3861L35.7895 23.2385C36.0687 23.5094 36.4424 23.6609 36.8314 23.6609C37.2203 23.6609 37.594 23.5094 37.8732 23.2385C38.0101 23.1017 38.1187 22.9393 38.1928 22.7605C38.2669 22.5818 38.305 22.3902 38.305 22.1967C38.305 22.0031 38.2669 21.8115 38.1928 21.6328C38.1187 21.454 38.0101 21.2916 37.8732 21.1548L30.5053 13.7869C30.226 13.5159 29.8522 13.3643 29.463 13.3643C29.0738 13.3643 28.7 13.5159 28.4207 13.7869L21.0528 21.1548C20.7844 21.4328 20.6359 21.805 20.6392 22.1914C20.6426 22.5777 20.7975 22.9473 21.0708 23.2205C21.344 23.4937 21.7136 23.6487 22.0999 23.6521C22.4863 23.6554 22.8594 23.5069 23.1373 23.2385Z"
                              fill="white"
                            />
                            <path
                              d="M42.7261 32.5127C42.3353 32.5127 41.9605 32.6679 41.6841 32.9443C41.4078 33.2206 41.2525 33.5955 41.2525 33.9863V38.407C41.252 38.7977 41.0966 39.1722 40.8204 39.4484C40.5441 39.7247 40.1696 39.8801 39.7789 39.8806H19.1488C18.7581 39.8801 18.3836 39.7247 18.1074 39.4484C17.8311 39.1722 17.6757 38.7977 17.6752 38.407V33.9863C17.6752 33.5955 17.5199 33.2206 17.2436 32.9443C16.9672 32.6679 16.5924 32.5127 16.2016 32.5127C15.8108 32.5127 15.436 32.6679 15.1596 32.9443C14.8833 33.2206 14.728 33.5955 14.728 33.9863V38.407C14.7293 39.5791 15.1955 40.7028 16.0243 41.5316C16.853 42.3603 17.9767 42.8265 19.1488 42.8278H39.7789C40.951 42.8265 42.0747 42.3603 42.9035 41.5316C43.7322 40.7028 44.1984 39.5791 44.1997 38.407V33.9863C44.1997 33.5955 44.0444 33.2206 43.7681 32.9443C43.4917 32.6679 43.1169 32.5127 42.7261 32.5127Z"
                              fill="white"
                            />
                          </svg> :
                          // <svg style={{ height: "50px", background: "green", borderRadius: "0" }} xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="40px" fill="#EFEFEF"><path d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" /></svg>
                          <svg className={profile.uplodedIcon} xmlns="http://www.w3.org/2000/svg" version="1.2" viewBox="0 0 133 129" width="133" height="129" >
                            <defs>
                              <image
                                width="90"
                                height="90"
                                id="img1"
                                href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFoAAABaCAMAAAAPdrEwAAAAAXNSR0IB2cksfwAAAEtQTFRFAAAA////////////////////////////////////////////////////////////////////////////////////////////////g5kLBwAAABl0Uk5TAAeM7fSZCbb/8vCOBrK4CAIBDaD39ZKH5jx3mNcAAAFjSURBVHic7dbNbsIwDADgeCBOO6BN4v2fbztMQzAkJtSJFda08X/MafY5/hS1jm0oDwtIOumkk0466VjuN84PoGFz9S7f8fQoFziF0ze5PH1F03e5rI7B9J88nIdY+hnGn1c28FlC6VaOohE5iMbkGBqVQ2hcjqAJOYCm5H6alLtpWu6lGbmT5uQ+mpW7aF7uoQW5g5ZkPy3KblqWvbRCdtIaeUZv4UMnNxNWoF+uK9U7c9QoV/TrUTxskid6uz4ojhvkij6sdQlaufogu70mRS9XtCrJINfFpyhWVT0jtJxokuevUUi1yYuHziYb5WUPYdKtctOeSMAst52PIOwy0lRRxCFj/RphPDI6ChrIJeNTZkH5ZGKAzTCnTM3Girs4ZXLsTi2u6Hudjp7ufQvrnbllYW7bZW4PqW2HzK44k+2R+e3pbrtkYTEbbZ8s7XywgzK8GatOR/dE0kknnXTS/57+Ac0vo1uyBeyGAAAAAElFTkSuQmCC"
                              />
                            </defs>

                            {/* Inline style via dangerouslySetInnerHTML */}
                            <style
                              dangerouslySetInnerHTML={{
                                __html: `.a { fill: #48b02c; }`
                              }}
                            />

                            <path
                              fillRule="evenodd"
                              className="a"
                              d="m2 0h129c1.1 0 2 0.9 2 2v125c0 1.1-0.9 2-2 2h-129c-1.1 0-2-0.9-2-2v-125c0-1.1 0.9-2 2-2z"
                            />
                            <use href="#img1" x="22" y="19" />
                          </svg>
                        }
                      </span>
                    </label>
                  </div>
                  <p className={profile.error}>{error.userimg}</p>
                </div>
              </div>

              <div className={profile.updateBtn}>
                <button type="submit" className="commonBtn">
                  Update
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

//  <div className={profile.Profield}></div>
